'use client'

import { ArrowUp, Square, X } from 'lucide-react'
import Image from 'next/image'
import * as React from 'react'

import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

import { ImageViewDialog } from './ImageViewDialog'
import { PromptInput, type PromptInputProps } from './PromptInput'
import { PromptInputAction } from './PromptInputActions'
import { PromptInputTextarea } from './PromptInputTextarea'
import { usePromptStyles } from './usePromptStyles'

type PromptInputBoxProps = Pick<PromptInputProps, 'className'> & {
  onSend?: (message: string, files?: File[]) => void
  isLoading?: boolean
  placeholder?: string
  value?: string
  onValueChange?: (value: string) => void
}

export const PromptInputBox = React.forwardRef<HTMLDivElement, PromptInputBoxProps>(
  (
    {
      onSend = () => {},
      isLoading = false,
      placeholder = 'Type your message here...',
      className,
      value,
      onValueChange
    },
    ref
  ) => {
    usePromptStyles()

    const [input, setInput] = React.useState(value ?? '')
    const [files, setFiles] = React.useState<File[]>([])
    const [filePreviews, setFilePreviews] = React.useState<Record<string, string>>({})
    const [selectedImage, setSelectedImage] = React.useState<string | null>(null)

    const isImageFile = React.useCallback((file: File) => file.type.startsWith('image/'), [])

    const processFile = React.useCallback(
      (file: File) => {
        if (!isImageFile(file) || file.size > 10 * 1024 * 1024) {
          return
        }

        setFiles([file])
        const reader = new FileReader()
        reader.onload = (event) => {
          const result = event.target?.result
          if (typeof result === 'string') {
            setFilePreviews({ [file.name]: result })
          }
        }
        reader.readAsDataURL(file)
      },
      [isImageFile]
    )

    const handleDragOver = React.useCallback((event: React.DragEvent) => {
      event.preventDefault()
      event.stopPropagation()
    }, [])

    const handleDragLeave = React.useCallback((event: React.DragEvent) => {
      event.preventDefault()
      event.stopPropagation()
    }, [])

    const handleDrop = React.useCallback(
      (event: React.DragEvent) => {
        event.preventDefault()
        event.stopPropagation()

        const droppedFiles = Array.from(event.dataTransfer.files)
        const imageFiles = droppedFiles.filter(isImageFile)
        if (imageFiles.length > 0) {
          processFile(imageFiles[0])
        }
      },
      [isImageFile, processFile]
    )

    const handleRemoveFile = React.useCallback(
      (index: number) => {
        const fileToRemove = files[index]
        if (fileToRemove && filePreviews[fileToRemove.name]) {
          setFilePreviews({})
        }
        setFiles([])
      },
      [filePreviews, files]
    )

    const openImageModal = React.useCallback((imageUrl: string) => {
      setSelectedImage(imageUrl)
    }, [])

    const handlePaste = React.useCallback(
      (event: ClipboardEvent) => {
        const items = event.clipboardData?.items
        if (!items) return

        for (let index = 0; index < items.length; index++) {
          const item = items[index]
          if (item.type.includes('image')) {
            const file = item.getAsFile()
            if (file) {
              event.preventDefault()
              processFile(file)
              break
            }
          }
        }
      },
      [processFile]
    )

    React.useEffect(() => {
      document.addEventListener('paste', handlePaste)
      return () => document.removeEventListener('paste', handlePaste)
    }, [handlePaste])

    React.useEffect(() => {
      if (value !== undefined) {
        setInput(value)
      }
    }, [value])

    const handleSubmit = React.useCallback(() => {
      if (!input.trim() && files.length === 0) {
        return
      }

      onSend(input, files)

      if (onValueChange) {
        onValueChange('')
      } else {
        setInput('')
      }

      setFiles([])
      setFilePreviews({})
    }, [files, input, onSend, onValueChange])

    const hasContent = input.trim().length > 0 || files.length > 0

    return (
      <>
        <PromptInput
          ref={(element) => {
            if (typeof ref === 'function') {
              ref(element)
            } else if (ref) {
              ref.current = element
            }
          }}
          value={input}
          onValueChange={onValueChange ?? setInput}
          isLoading={isLoading}
          onSubmit={handleSubmit}
          className={className}
          disabled={isLoading}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {files.length > 0 && (
            <div className="flex flex-wrap gap-2 pb-1 transition-all duration-300">
              {files.map((file, index) => {
                const preview = filePreviews[file.name]
                if (!file.type.startsWith('image/') || !preview) {
                  return null
                }

                return (
                  <div key={file.name} className="group relative">
                    <div
                      className="h-16 w-16 cursor-pointer overflow-hidden rounded-xl transition-all duration-300"
                      onClick={() => openImageModal(preview)}
                    >
                      <Image
                        src={preview}
                        alt={file.name}
                        width={64}
                        height={64}
                        className="h-full w-full object-cover"
                      />
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation()
                          handleRemoveFile(index)
                        }}
                        className="absolute right-1 top-1 rounded-full bg-black/70 p-0.5"
                      >
                        <X className="h-3 w-3 text-white" />
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
          )}

          <div className="flex items-center">
            <div className="flex-1">
              <PromptInputTextarea placeholder={placeholder} disableAutosize={false} />
            </div>

            <PromptInputAction
              tooltip={
                isLoading
                  ? 'Stop generation'
                  : hasContent
                  ? 'Send message'
                  : 'Type a message to send'
              }
            >
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className={cn(
                  'h-10 w-10 rounded-full !bg-black !text-white transition-all duration-200 hover:!bg-gray-800',
                  hasContent
                    ? 'shadow-[0_4px_15px_rgba(0,0,0,0.3),inset_0_1px_0_rgba(255,255,255,0.1),0_0_10px_rgba(0,0,0,0.4)] hover:shadow-[0_8px_20px_rgba(0,0,0,0.5),inset_0_1px_0_rgba(255,255,255,0.2),0_0_20px_rgba(0,0,0,0.6)]'
                    : 'shadow-[0_4px_15px_rgba(0,0,0,0.2),inset_0_1px_0_rgba(255,255,255,0.1),0_0_10px_rgba(0,0,0,0.3)] hover:shadow-[0_8px_20px_rgba(0,0,0,0.3),inset_0_1px_0_rgba(255,255,255,0.2),0_0_20px_rgba(0,0,0,0.5)]'
                )}
                onClick={() => {
                  if (hasContent) {
                    handleSubmit()
                  }
                }}
                disabled={isLoading || !hasContent}
              >
                {isLoading ? <Square className="h-4 w-4 fill-white animate-pulse" /> : <ArrowUp className="h-4 w-4" />}
              </Button>
            </PromptInputAction>
          </div>
        </PromptInput>

        <ImageViewDialog imageUrl={selectedImage} onClose={() => setSelectedImage(null)} />
      </>
    )
  }
)

PromptInputBox.displayName = 'PromptInputBox'
